import React, { RefObject, useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import momentTz from "moment-timezone";
import useResizeObserver from "use-resize-observer/polyfilled";
import { colors } from "@sonder/ui/source/styled/theme";

interface TimeRangeProps {
  className?: string;
  date?: moment.Moment;
  endAt?: moment.Moment;
  height?: number;
  range?: moment.unitOfTime.DurationConstructor;
  startAt?: moment.Moment;
  timezone?: string;
}

const StyledTimeRange = styled.div`
  display: flex;
  font-size: 0.85rem;
  justify-content: space-between;
  min-width: 0;
  padding: 0.25rem 0;
  position: relative;
  width: 100%;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const TimeContainer = styled.div`
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
`;

const TimeBar = styled.div`
  border-left: 1px dashed ${colors.grayA};
  font-size: 0.7rem;
  padding-bottom: 3rem;
  position: absolute;
  top: 1.75rem;
`;

const TimeBox = styled.div`
  align-items: center;
  background-color: ${colors.gray1};
  border-radius: 4px;
  color: white;
  display: flex;
  height: 1.43rem;
  justify-content: center;
  position: absolute;
  transform: translateX(-50%);
  width: 3.71rem;
`;

interface CurrentTimerBarProps {
  timezone?: string;
  endAt: moment.Moment;
  height?: number;
  parentLayerWidth?: number;
  startAt: moment.Moment;
}

const CurrentTimeBar = ({
  timezone = momentTz.tz.guess(),
  endAt,
  height,
  parentLayerWidth = 0,
  startAt,
}: CurrentTimerBarProps) => {
  const [now, setNow] = useState(momentTz.tz(timezone));
  const timePosition = startAt.diff(now) / startAt.diff(endAt);
  const isTimeBarVisible =
    now.isSameOrBefore(endAt) && now.isSameOrAfter(startAt);

  useEffect(() => {
    // update now when timezone changes
    setNow(momentTz.tz(timezone));
    // update now every 30 sec
    const interval = setInterval(() => setNow(momentTz.tz(timezone)), 30000);
    return () => clearInterval(interval);
  }, [timezone]);
  if (!isTimeBarVisible) {
    return null;
  }

  return (
    <TimeContainer>
      <TimeBar
        style={{
          height: `${height || 0}px`,
          transform: `translate3d(${timePosition * parentLayerWidth}px, 0, 0)`,
        }}
      >
        <TimeBox> {now.format("h:mm a")}</TimeBox>
      </TimeBar>
    </TimeContainer>
  );
};

export const TimeRange = ({
  className,
  timezone = momentTz.tz.guess(),
  date = moment(),
  endAt = moment().endOf("day"),
  height = 0,
  range = "hours",
  startAt = moment().startOf("day"),
}: TimeRangeProps) => {
  const { ref, width: parentLayerWidth = 0 } = useResizeObserver();
  const startHour = startAt.hours();
  const now = date.clone().seconds(0).minutes(0).hours(startHour);
  const totalUnits = endAt.clone().add(1, "s").diff(startAt, range);
  const minWidth = Math.round(parentLayerWidth / totalUnits);

  return (
    <StyledTimeRange
      className={className}
      ref={ref as RefObject<HTMLDivElement>}
    >
      <CurrentTimeBar
        endAt={endAt}
        height={height}
        parentLayerWidth={parentLayerWidth}
        startAt={startAt}
        timezone={timezone}
      />
      {new Array(totalUnits).fill(undefined).map((a, index) => {
        const time = now.hours(startHour + index);
        return (
          <span key={time.toISOString()} style={{ minWidth: `${minWidth}px` }}>
            {time.format("ha")}
          </span>
        );
      })}
    </StyledTimeRange>
  );
};
